import { IEventModule } from "app/event/model"
import { APP_CONTEXTS } from "app/model"
import { AUCTION_TYPES, IAuction } from "auction/model"
import { IAudienceAssociation } from "audience/model"
import { CAMPAIGN_STATUSES, ICampaign, IDuplicateFormData } from "campaign/model"
import { ICap } from "cap/model"
import { ICoordinate, ICoordinateData } from "coordinate/model"
import { CURRENCIES } from "currency/model"
import { IDspDealAdvertiserDomain, IDspDealFormat, IDspDealSeat } from "dsp-deal/model"
import { IEnvironment, IEnvironmentAssociation, IEnvironmentsForecast } from "environments/model"
import { DISTANCE_UNITS } from "form/map/model"
import { FormikErrors } from "formik"
import { Feature, MultiPolygon } from "geojson"
import {
  APPROVAL_STATUSES,
  ICreativeFileType,
  IMedia,
  IMediaCreativeForecast,
  IMediaCreativeForecastById
} from "media/model"
import { IOperator, IOperatorsForecast } from "operator/model"
import { CAMPAIGN_CREATE_PATH, CAMPAIGN_EDIT_PATH, FORECAST_CREATE_PATH, FORECAST_EDIT_PATH } from "page/model"
import { ICampaignSchedule, ICampaignScheduleFormItem, IWeather, WEATHERS } from "schedule/model"
import {
  IGeoShape,
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState
} from "shape/model"
import { ICampaignSspDeal, ISspAssociation, ISspDeal } from "ssp/model"
import { IUserTag } from "user-tag/model"
import { IFormInventory } from "./inventory/model"

export const CANCELED_REJECTION: string = "canceled"

export const USER_PREF_ENVIRONMENT_IS_TREE_VIEW = "form-environment-is-tree-view"

export const DEFAULT_DSP_DEAL_AUCTION_TYPE = AUCTION_TYPES.FIRST_PRICE

export const DEFAULT_FORM_CURRENCY = CURRENCIES.USD

export const ALL_FORM_PAGE_PATHS: string[] = [
  CAMPAIGN_EDIT_PATH,
  CAMPAIGN_CREATE_PATH,
  FORECAST_CREATE_PATH,
  FORECAST_EDIT_PATH
]

export const GEO_TARGETS_FORM_WIDTH: string = "18rem"
export const GEO_TARGETS_MAP_HEIGHT = "30rem"
export const GEO_TARGETS_FILE_IMPORT_POPUP_MAX_WIDTH = 1000
export const CREATIVE_POPUP_MAX_WIDTH = 600
export const CONFIGURATION_CHANGE_LABEL_KEY = "campaign.title"

export const CREATIVE_FORECAST_VIDEO_MEDIA_NAME = "forecast-video"

export const COORDINATE_CSV_COLUMN_NAME = "name"
export const COORDINATE_CSV_COLUMN_LATITUDE = "latitude"
export const COORDINATE_CSV_COLUMN_LONGITUDE = "longitude"
export const COORDINATE_CSV_COLUMN_RADIUS = "radius"
export const REQUIRED_COORDINATE_CSV_COLUMNS: string[] = [
  COORDINATE_CSV_COLUMN_NAME,
  COORDINATE_CSV_COLUMN_LATITUDE,
  COORDINATE_CSV_COLUMN_LONGITUDE,
  COORDINATE_CSV_COLUMN_RADIUS
]

export const SHAPE_CSV_COLUMN_TYPE = "type"
export const SHAPE_CSV_COLUMN_NAME = "name"
export const SHAPE_CSV_COLUMN_STATE_ID = "state_id"
export const SHAPE_CSV_COLUMN_COUNTRY_ID = "country_id"
export const SHAPE_CSV_COLUMN_POSTAL_CODE = "postal_code"
export const SHAPE_CSV_COLUMN_NAME_TYPE = "Type"
export const SHAPE_CSV_COLUMN_NAME_NAME = "Name"
export const SHAPE_CSV_COLUMN_NAME_CITY = "City"
export const SHAPE_CSV_COLUMN_NAME_STATE = "State"
export const SHAPE_CSV_COLUMN_NAME_COUNTRY = "Country"

export const REQUIRED_POSTAL_CODE_CSV_COLUMNS: string[] = [SHAPE_CSV_COLUMN_POSTAL_CODE]

export const DISTANCE_UNIT_BY_CURRENCY = {
  [CURRENCIES.CAD]: DISTANCE_UNITS.MILES,
  [CURRENCIES.USD]: DISTANCE_UNITS.MILES,
  [CURRENCIES.EUR]: DISTANCE_UNITS.KILOMETERS
}

export enum GEO_TARGETS_TYPES {
  COORDINATE = "coordinate",
  POSTAL_CODE = "postal-code",
  CITY = "city",
  STATE = "state",
  COUNTRY = "country",
  METROPOLITAN = "metropolitan"
}
export const ALL_GEO_TARGETS_TYPES: GEO_TARGETS_TYPES[] = [
  GEO_TARGETS_TYPES.COORDINATE,
  GEO_TARGETS_TYPES.CITY,
  GEO_TARGETS_TYPES.POSTAL_CODE,
  GEO_TARGETS_TYPES.STATE,
  GEO_TARGETS_TYPES.COUNTRY
]

export enum SELECT_FILTER_VALUES {
  ALL = "all",
  SELECTED = "selected",
  NOT_SELECTED = "not-selected"
}
export const ALL_SELECT_FILTER_VALUES: SELECT_FILTER_VALUES[] = [
  SELECT_FILTER_VALUES.ALL,
  SELECT_FILTER_VALUES.SELECTED,
  SELECT_FILTER_VALUES.NOT_SELECTED
]

export enum GEO_TARGETS_FORM_TABS {
  COORDINATE = "COORDINATE",
  SHAPE = "SHAPE"
}
export const ALL_GEO_TARGETS_FORM_TABS: GEO_TARGETS_FORM_TABS[] = [
  GEO_TARGETS_FORM_TABS.COORDINATE,
  GEO_TARGETS_FORM_TABS.SHAPE
]
export const enum FORM_TABS {
  CONFIGURATION = "configuration",
  GEO_TARGETS = "geo-targets",
  SCREENS = "screens",
  AUDIENCE = "audience",
  CREATIVES = "creatives"
}
export const ALL_FORM_TABS: FORM_TABS[] = [
  FORM_TABS.CONFIGURATION,
  FORM_TABS.GEO_TARGETS,
  FORM_TABS.SCREENS,
  FORM_TABS.AUDIENCE,
  FORM_TABS.CREATIVES
]

export const enum FORM_TAB_BOXES {
  CONFIGURATION_MAIN = "configuration-main",
  CONFIGURATION_BUDGET = "configuration-budget",
  CONFIGURATION_SCHEDULE = "configuration-schedule",
  CONFIGURATION_CAPS = "configuration-caps",
  SCREENS_ENVIRONMENTS = "screens-environments",
  SCREENS_SSP = "screens-ssp",
  SCREENS_SSP_DEALS = "screens-ssp-deals",
  CREATIVE_CAMPAIGN_LIST = "creative-campaign-list",
  CREATIVE_FORECAST_LIST = "creative-forecast-list",
  CREATIVE_DSP_DEAL_LIST = "creative-dsp-deal-list"
}

export enum FORM_SAVE_ERRORS {
  SERVER = "server",
  INVALID_FORM = "invalid-form"
}

export enum FORM_EVENTS {
  SSP_SELECTION_CHANGED = "SSP_SELECTION_CHANGED",
  ENVIRONMENTS_SELECTION_CHANGED = "ENVIRONMENTS_SELECTION_CHANGED",
  FORM_CLEARED = "FORM_CLEARED",
  GEO_TARGET_CHANGE = "GEO_TARGET_CHANGE",
  FORM_OPENED = "FORM_OPENED",
  FORM_CLOSED = "FORM_CLOSED",
  SUMMARY_OPENED = "SUMMARY_OPENED",
  SUMMARY_CLOSED = "SUMMARY_CLOSED"
}

export enum SUMMARY_TABS {
  PARAMETERS = "PARAMETERS",
  REPORT_OPERATORS = "REPORT_OPERATORS",
  REPORT_CREATIVES = "REPORT_CREATIVES",
  REPORT_SHAPES = "REPORT_SHAPES",
  REPORT_COORDINATES = "REPORT_COORDINATES"
}
export const ALL_SUMMARY_TABS: SUMMARY_TABS[] = [
  SUMMARY_TABS.PARAMETERS,
  SUMMARY_TABS.REPORT_OPERATORS,
  SUMMARY_TABS.REPORT_CREATIVES,
  SUMMARY_TABS.REPORT_SHAPES,
  SUMMARY_TABS.REPORT_COORDINATES
]

export type IFormDataLoader = (form: IForm, onFinish: (error?: string) => any) => any

export type IFormError = "" | "loading" | "not-found"

export interface IFormCoordinate extends ICoordinate {
  isProcessingServerSide?: boolean // creating, updating, or deleting
  hasLoadingError?: boolean
}

export interface IFormSetParams {
  form: IForm
  lastSavedData?: IFormData
  tempCampaignId?: number
  tempDspDealId?: number
  changes?: Partial<IFormChanges>
}

export interface IFormConfigurationMain extends IDuplicateFormData {
  name: string
  description: string | null
  max_views: number
  unlimitedImpressions: boolean
  userTagIds: number[]
  isBiddingOnlyOnDeals: boolean
}

export interface IFormConfigurationBudget {
  budget: number | null
  max_daily_spend: number | null
  max_bid: number | null
  auctions: AUCTION_TYPES[]
  hasAuctionError: boolean
  equalizeDailyMaxSpendFromSchedule: boolean
  currency_id: CURRENCIES
}

export interface IFormConfigurationSchedule {
  min_celsius: number | null
  max_celsius: number | null
  scheduleItems: ICampaignScheduleFormItem[]
  noSchedule: boolean
  weathers: WEATHERS[]
}

export interface IFormConfigurationCaps {
  minute: ICap | null
  hour: ICap | null
  day: ICap | null
  week: ICap | null
  month: ICap | null
}

export interface IFormConfigurationDspDeal {
  id: null | number
  dealid: null | string
  description: null | string
  dsp_id: null | number
  bid_floor: number
  auction_type: number
  formatIds: number[]
  advertiserDomainIds: number[]
  seatIds: number[]
  isSynchronizingFormat: boolean
}

export interface IFormConfigurationModifiedData {
  main: Partial<IFormConfigurationMain>
  budget: Partial<IFormConfigurationBudget>
  schedule: Partial<IFormConfigurationSchedule>
  caps: Partial<IFormConfigurationCaps>
  dspDeal: Partial<IFormConfigurationDspDeal>
}

export interface IFormConfiguration {
  main: IFormConfigurationMain
  budget: IFormConfigurationBudget
  schedule: IFormConfigurationSchedule
  caps: IFormConfigurationCaps
  dspDeal: IFormConfigurationDspDeal
}

export interface IGeoTargetsFileImport {
  isPopupOpened: boolean
  files: FileList | null
  rows: ICoordinateData[] | string[] | null
  isParsing: boolean
  parsingError: string
  error: string
  isSaving: boolean
  geoTargetsType: GEO_TARGETS_TYPES
  importType: "replace" | "insert"
}

export interface IFormGeoTargets {
  coordinates: IFormCoordinate[]
  cities: IGeoShapeCity[]
  postalCodes: IGeoShapePostalCode[]
  states: IGeoShapeState[]
  countries: IGeoShapeCountry[]
  metropolitans: IGeoShapeMetropolitan[]
  fileImport: IGeoTargetsFileImport
}

export interface IFormScreens {
  environmentsIds: number[]
  environmentFilter: string
  environmentSelectFilter: SELECT_FILTER_VALUES
  environmentExpandedIds: number[]
  isSynchronizingEnvironments: boolean
  isLoadingEnvironmentsForecast: boolean
  hasEnvironmentsSynchronizeError: boolean
  sspIds: number[]
  isLoadingSsp: boolean
  isSynchronizingSsp: boolean
  hasSspSynchronizeError: boolean
  allSspDeals: ISspDeal[]
  campaignSspDeals: ICampaignSspDeal[]
  isLoadingAllSspDeals: boolean
  isSynchronizingSspDeal: boolean
  hasSspDealSynchronizeError: boolean
  hasLoadingAllSspDealsError: boolean
  allOperators: IOperator[]
  isLoadingAllOperators: boolean
  hasLoadingAllOperatorsError: boolean
  operators: IOperator[]
  isSynchronizingOperator: boolean
  isLoadingOperatorsForecast: boolean
  hasOperatorSynchronizeError: boolean
  operatorFilter: string
  operatorSelectFilter: SELECT_FILTER_VALUES
}

export interface IFormAudience {
  selectedIds: number[]
  selectFilter: SELECT_FILTER_VALUES
  filter: string // list filter
  isSynchronizing: boolean
  hasSynchronizeError: boolean
}

export interface IFormCreatives {
  filter: SELECT_FILTER_VALUES
  medias: IMedia[]
  isLoadingForecast: boolean
  hasLoadingForecastError: boolean
  selectedRequiredMediaCreativeIds: string[] // not used for campaign, only for forecast
  isSynchronizing: boolean
  hasSynchronizeError: boolean
}

export interface IFormContext<T> {
  isValid: boolean
  errors: FormikErrors<T>
}

export interface IFormRelations {
  auctions: IAuction[] | null
  schedule: ICampaignSchedule | null
  weathers: IWeather[] | null
  caps: ICap[] | null
  coordinates: ICoordinate[]
  cities: IGeoShapeCity[]
  states: IGeoShapeState[]
  countries: IGeoShapeCountry[]
  metropolitans: IGeoShapeMetropolitan[]
  postalCodes: IGeoShapePostalCode[]
  environments: IEnvironmentAssociation[] | null
  ssps: ISspAssociation[] | null
  operators: IOperator[]
  campaignSspDeals: ICampaignSspDeal[]
  audiences: IAudienceAssociation[]
  userTags: IUserTag[]
  dspDealAdvertiserDomains: IDspDealAdvertiserDomain[]
  dspDealFormats: IDspDealFormat[]
  dspDealSeats: IDspDealSeat[]
  // errors
  auctionsError: boolean
  scheduleError: boolean
  weathersError: boolean
  capsError: boolean
  environmentsError: boolean
  sspsError: boolean
  operatorsError: boolean
  sspDealsError: boolean
  audiencesError: boolean
  dspDealFormatsError: boolean
  userTagsError: boolean
}

export interface IFormContexts {
  appContext: APP_CONTEXTS
  isCreation: boolean
  isForecast: boolean
  isDspDeal: boolean
  originalCampaign: ICampaign | null // the original campaign not the temporary one
  isPaused: boolean
  isSavingCampaign: boolean
  isSavingTempCampaign: boolean
  refreshFormikForm: boolean
  relations: IFormRelations
  saveError: FORM_SAVE_ERRORS | null
  initialData: IFormData | null
  lastSavedData: IFormData | null
  configuration: IFormContext<IFormConfiguration>
  screens: IFormContext<IFormScreens>
  geoTargets: IFormContext<IFormGeoTargets>
  creatives: IFormContext<IFormCreatives>
  environment: {
    initialForecast: IEnvironmentsForecast | null // null means creation form
    currentForecast: IEnvironmentsForecast
  }
  operator: {
    initialForecast: IOperatorsForecast | null // null means creation form
    currentForecast: IOperatorsForecast
  }
  mediaCreative: {
    initialForecast: IMediaCreativeForecastById | null // null means creation form
    currentForecast: IMediaCreativeForecastById
  }
}

export interface IFormData {
  configuration: IFormConfiguration
  geoTargets: IFormGeoTargets
  screens: IFormScreens
  audience: IFormAudience
  creatives: IFormCreatives
}

export interface IForm extends IFormData {
  activeTab: FORM_TABS
  activeSummaryTab: SUMMARY_TABS
  campaignId: number
  tempCampaignId: number
  tempDspDealId: number
  statusId: CAMPAIGN_STATUSES
  context: IFormContexts
  changes: IFormChanges
}

export interface IFormServiceState {
  saveFormTimeout: any
  pendingFormToSave: IForm | null
  sspTimeout: any
  sspIsSynchronizing: boolean
  sspToApplySelection: number[] | null
  environmentsTimeout: any
  environmentsIsSynchronizing: boolean
  environmentsToApplySelection: number[] | null
  environmentsForecastTimeout: any
  environmentsForecastIsLoading: boolean
  environmentsForecastRefreshAgain: boolean
  operatorRunId: number
  audienceTimeout: any
  audienceIsSynchronizing: boolean
  audienceToApplySelection: number[] | null
  operatorTimeout: any
  operatorIsSynchronizing: boolean
  operatorToApplySelection: IOperator[] | null
  sspDealsTimeout: any
  sspDealsIsSynchronizing: boolean
  sspDealsToApplySelection: ICampaignSspDeal[] | null
  isInventoryRefreshing: boolean
  campaignIdToRefreshForInventory: number | null | undefined
  tabBoxIsOpenedById: { [boxId in FORM_TAB_BOXES]: boolean }
  photoResizeTimeOut: any
  mediaCreativeForecastIsLoading: boolean
  mediaCreativeForecastRefreshAgain: boolean
  mediaCreativeForecastTimeout: any
  mediaCreativeForecastIsSynchronizing: boolean
  mediaCreativeForecastToApplySelection: string[] | null
}

export interface IGeoTargetsListItem {
  id: number | string
  name: string
  latitude: number | null
  longitude: number | null
  radius: number | null
  type: GEO_TARGETS_TYPES
  coordinate?: IFormCoordinate
  shape?: IGeoShape
  state_id?: string
  country_id?: string
}

export interface IGeoTargetsForm {
  context: APP_CONTEXTS
  isForecast: boolean
  activeTab: GEO_TARGETS_FORM_TABS
  isCreation: boolean
  name: string
  latitude: null | string
  longitude: null | string
  radius: null | string
  toUpdateEntityId: number
  toAddShape: IGeoShape | null
  selection: IGeoTargetsSelection
  isSelectingShape: boolean // we need to fetch the shape geometry from server when selecting
  isDeletingSelection: boolean
  noIntersectionError: boolean
}

export interface IGeoTargetsSelection {
  cityIds: string[]
  postalCodeIds: string[]
  coordinateIds: number[]
  stateIds: string[]
  countryIds: string[]
  metropolitanIds: string[]
}

export interface IFormDataLoadStatus {
  isLoading: boolean
  error: IFormError
}

export interface IAppForm extends IFormDataLoadStatus {
  form: IForm
  inventory: IFormInventory
  geoTargetsForm: IGeoTargetsForm
}

export interface IFormState {
  campaignCreateForm: IAppForm
  campaignEditForm: IAppForm
  forecastCreateForm: IAppForm
  forecastEditForm: IAppForm
  dspDealCreateForm: IAppForm
  dspDealEditForm: IAppForm
  isEnvironmentTreeView: boolean
}

export interface IFormChanges {
  configuration: IFormChange[]
  geoTargets: IFormChange[]
  screens: IFormChange[]
  audience: IFormChange[]
  creative: IFormChange[]
}

export interface IFormChange {
  labelKey: string // The label that will be displayed ex: budget, ssp, audience, cap, etc...
  hasChange: boolean
  changeCount: number
  addedItems: string[]
  updatedItems: string[]
  deletedItems: string[]
}

export interface IFormEventModule extends IEventModule {
  on: {
    sspSelectionChanged(listenerId: string, fn: (newSelection: number[]) => void): void
    environmentsSelectionChanged(listenerId: string, fn: (newSelection: number[]) => void): void
    formCleared(listenerId: string, fn: (oldForm: IForm) => void): void
    geoTargetChange(listenerId: string, fn: (params: IGeoTargetChangeParams) => void): void
    formOpened(listenerId: string, fn: (form: IForm) => void): void
    formClosed(listenerId: string, fn: (form: IForm) => void): void
  }
}

export interface IFormEnvironmentsRow {
  id: number
  name: string
  impression: number
  impressionRatio: string
  impressionPercent: number
  impressionDiff: number // 0 = no change, > 0 increase, < 0 decrease
  screens: number
  screensRatio: string
  screensPercent: number
  screensDiff: number // 0 = no change, > 0 increase, < 0 decrease
  environment: IEnvironment
  isIndeterminate: boolean // use for tree checkbox
}

export interface IFormOperatorRow {
  id: string // combination of publisher & ssp ids
  publisherId: number
  publisherName: string
  sspId: number
  cpm: number
  impression: number
  impressionRatio: string
  impressionPercent: number
  impressionDiff: number // 0 = no change, > 0 increase, < 0 decrease
  screens: number
  screensRatio: string
  screensPercent: number
  screensDiff: number // 0 = no change, > 0 increase, < 0 decrease
}

export interface IFormMediaCreativeForecastRow extends IMediaCreativeForecast {
  impressionRatio: string
  impressionDiff: number // 0 = no change, > 0 increase, < 0 decrease
  screensRatio: string
  screensDiff: number // 0 = no change, > 0 increase, < 0 decrease
  isUploaded: boolean
}

export interface IFormCreativeRow {
  id: string
  mediaId: number
  campaignMediaId: number
  creativeId: number
  name: string
  createdAt: Date | null
  type: ICreativeFileType
  approval_status_id?: APPROVAL_STATUSES
  refusal_note?: string | null
}

export interface IFormMediaCreativeCampaignRow extends IFormMediaCreativeForecastRow, IFormCreativeRow {
  // nothing
}

export interface IGeoTargetChangeParams {
  form: IForm
  intersectionFeature: Feature<MultiPolygon> | null
  hasNoIntersectionError: boolean
}

export interface IFormEnvironmentTreeInfo {
  isSelected: boolean
  hasOneParentSelected: boolean
  isTargeted: boolean
  isDisabled: boolean
}

export interface IReportSSPOperatorRow {
  sspId: number
  sspName: string
  operatorName: string
  impressions_with_multiplier: number
  impressions_without_multiplier: number
  spend: number
  creatives: number
  cpm: number
}

export interface IReportCreativeRow {
  type: "image" | "video"
  name: string
  id: number
  dimension: string
  impressions_with_multiplier: number
  impressions_without_multiplier: number
  screens: number
}

export interface IReportShapeRow {
  type: GEO_TARGETS_TYPES
  name: string
  id: number
  impressions_with_multiplier: number
  impressions_without_multiplier: number
  screens: number
  spend: number
  cpm: number
  venueTypeId: number
  venueTypeName: string
}

export interface IReportCoordinateRow {
  name: string
  id: number
  latitude: number
  longitude: number
  radius: number
  impressions_with_multiplier: number
  impressions_without_multiplier: number
  spend: number
  cpm: number
}

export interface ISummaryPageInfo {
  form: IForm
  contentWidth: number
  isForecast: boolean
  isDspDeal: boolean
}
